import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PlansModal from './PlansModal';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
  WalletIcon, MagnifyingGlassIcon, DocumentArrowUpIcon,
  ClockIcon, ArrowTrendingUpIcon, CheckBadgeIcon,
  ArrowRightIcon, BoltIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { apiClient } from '../../lib/axios';
import { useAuthStore } from '../../store/useAuthStore';

interface RecentSearch {
  id: string;
  query: string;
  type: string;
  people_found: number;
  status: string;
}

interface DashboardStats {
  credits_left: number;
  recent_searches_count: number;
  recent_searches: RecentSearch[];
}

function StatCard({ icon: Icon, label, value, sub, accent = false }: {
  icon: React.ElementType; label: string; value: string | number; sub?: string; accent?: boolean;
}) {
  return (
    <div className={`rounded-2xl p-6 flex flex-col gap-4 ${accent ? 'bg-brand-500 text-slate-900 shadow-lg shadow-brand-500/20' : 'bg-white border border-slate-200 shadow-sm'}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent ? 'bg-slate-900/10' : 'bg-slate-50 border border-slate-100'}`}>
        <Icon className={`w-5 h-5 ${accent ? 'text-slate-900' : 'text-slate-500'}`} />
      </div>
      <div>
        <p className={`text-[11px] font-bold uppercase tracking-wide ${accent ? 'text-slate-700' : 'text-slate-400'}`}>{label}</p>
        <p className={`text-3xl font-extrabold mt-1 leading-none ${accent ? 'text-slate-900' : 'text-slate-900'}`}>{value}</p>
        {sub && <p className={`text-xs mt-1.5 font-medium ${accent ? 'text-slate-600' : 'text-slate-400'}`}>{sub}</p>}
      </div>
    </div>
  );
}

function statusBadge(status: string) {
  const s = status.toLowerCase();
  if (s === 'complete' || s === 'completed')
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full uppercase tracking-wider"><CheckCircleIcon className="w-3 h-3" />Done</span>;
  if (s === 'failed')
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 text-[10px] font-bold rounded-full uppercase tracking-wider"><ExclamationCircleIcon className="w-3 h-3" />Failed</span>;
  return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full uppercase tracking-wider"><span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />Active</span>;
}

export default function Dashboard() {
  useDocumentTitle('Dashboard', 'Your Localcontact intelligence overview, credits, recent searches, and quick actions.');
  const { user } = useAuthStore();
  const [isPlansOpen, setIsPlansOpen] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({ credits_left: 0, recent_searches_count: 0, recent_searches: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/user/dashboard-stats');
        setStats({
          credits_left: res.data.credits_left ?? 0,
          recent_searches_count: res.data.recent_searches_count ?? 0,
          recent_searches: res.data.recent_searches ?? [],
        });
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  const activeCount = stats.recent_searches.filter(s => s.status.toLowerCase() !== 'complete' && s.status.toLowerCase() !== 'failed').length;

  return (
    <div className="h-full flex flex-col bg-slate-50/50">
      <div className="flex-1 px-6 md:px-10 py-8 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto flex flex-col gap-8">

          {/* Header */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
              </h1>
              <p className="text-sm text-slate-500 mt-1">Here's your intelligence overview.</p>
            </div>
            <button
              onClick={() => setIsPlansOpen(true)}
              className="flex items-center gap-2 bg-brand-500 text-slate-900 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors"
            >
              <BoltIcon className="w-4 h-4" /> Get Credits
            </button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <StatCard accent icon={WalletIcon} label="Credits Balance" value={(stats.credits_left ?? user?.credits ?? 0).toLocaleString()} sub="Available for searches" />
            <StatCard icon={MagnifyingGlassIcon} label="Total Searches" value={loading ? '—' : stats.recent_searches_count} sub="All time" />
            <StatCard icon={ClockIcon} label="Active Jobs" value={loading ? '—' : activeCount} sub="Currently running" />
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/dashboard/search"
              className="flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:border-brand-300 hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center">
                  <MagnifyingGlassIcon className="w-5 h-5 text-brand-500" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">Single Search</p>
                  <p className="text-xs text-slate-400 mt-0.5">Find leads for one company</p>
                </div>
              </div>
              <ArrowRightIcon className="w-4 h-4 text-slate-500 group-hover:text-brand-500 transition-colors shrink-0" />
            </Link>

            <Link
              to="/dashboard/bulk-upload"
              className="flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:border-brand-300 hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center">
                  <DocumentArrowUpIcon className="w-5 h-5 text-violet-500" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">Bulk Upload</p>
                  <p className="text-xs text-slate-400 mt-0.5">Process hundreds of companies</p>
                </div>
              </div>
              <ArrowRightIcon className="w-4 h-4 text-slate-500 group-hover:text-brand-500 transition-colors shrink-0" />
            </Link>
          </div>


          {/* Recent activity */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ArrowTrendingUpIcon className="w-5 h-5 text-slate-400" />
                <h3 className="font-extrabold text-slate-900 text-base">Recent Activity</h3>
              </div>
              <Link to="/dashboard/search_history" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-900 bg-brand-500 rounded-lg hover:bg-brand-600 transition-colors">
                View all <ArrowRightIcon className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50/80 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.12em] border-b border-slate-100">
                    <th className="px-6 py-4">Search Query</th>
                    <th className="px-6 py-4">Type</th>
                    <th className="px-6 py-4">People Found</th>
                    <th className="px-6 py-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <tr key={i}>
                        {[140, 80, 100, 80].map((w, j) => (
                          <td key={j} className="px-6 py-4">
                            <div className="h-3.5 bg-slate-100 rounded animate-pulse" style={{ width: w }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : stats.recent_searches.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <CheckBadgeIcon className="w-10 h-10 text-slate-400" />
                          <p className="text-slate-400 text-sm font-medium">No recent activity yet.</p>
                          <Link to="/dashboard/search" className="text-brand-500 text-sm font-bold hover:underline">Start your first search →</Link>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    stats.recent_searches.map(search => (
                      <tr key={search.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4">
                          <p className="font-semibold text-slate-900 text-sm">{search.query}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full ${
                            search.type.toLowerCase().includes('bulk') ? 'bg-violet-50 text-violet-600' : 'bg-brand-50 text-brand-600'
                          }`}>{search.type}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-[13px] font-bold text-slate-900">
                            {search.people_found > 0 ? search.people_found : <span className="text-slate-400 font-medium">—</span>}
                          </span>
                        </td>
                        <td className="px-6 py-4">{statusBadge(search.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <PlansModal isOpen={isPlansOpen} onClose={() => setIsPlansOpen(false)} onPurchaseSuccess={(n) => setStats(p => ({ ...p, credits_left: p.credits_left + n }))} />
    </div>
  );
}
